import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ConnectableProvider } from '../../types/connected-apps';

export interface RenderProviderIconOptions {
  readonly provider: ConnectableProvider;
  readonly size: number;
}

interface ConnectedAppsContextValue {
  readonly providers: ConnectableProvider[];
  /**
   * Supplied by the host so brand marks get the same `logo_url -> bundled
   * asset -> generic icon` fallback as every other surface. Catalog
   * `logo_url` is frequently empty, so rendering it raw here would show
   * broken images. The fallback logic lives in the console (it owns the
   * bundled assets), which this library cannot import.
   */
  readonly renderIcon?: (options: RenderProviderIconOptions) => ReactNode;
  /** Route the user to the Connected Apps settings page. */
  readonly onManage?: () => void;
  /**
   * Start the OAuth flow for one provider directly — used by the row-level
   * Connect/Reconnect actions, which must not detour through the settings
   * modal the way "Connect more"/"Discover more apps" do.
   */
  readonly onConnect?: (provider: string) => void;
  /** Mute/unmute an app. Absent when the host cannot perform it. */
  readonly onSetEnabled?: (provider: string, enabled: boolean) => void;
  /** The tenant has enabled web search, so the composer may show its switch.
   *  Absent or false renders no control at all — never a dead toggle. */
  readonly webSearchAvailable?: boolean;
}

const ConnectedAppsContext = createContext<ConnectedAppsContextValue>({
  providers: [],
});

export interface ConnectedAppsProviderProps extends ConnectedAppsContextValue {
  readonly children: ReactNode;
}

/**
 * Carries the caller's connectable apps into the composer chip.
 *
 * The console owns the React Query hooks and routing; this library stays
 * data-agnostic, mirroring `SourcesProvider`. A host that never mounts this
 * renders an inert chip rather than an error.
 */
export function ConnectedAppsProvider({
  providers,
  renderIcon,
  onManage,
  onConnect,
  onSetEnabled,
  webSearchAvailable,
  children,
}: ConnectedAppsProviderProps) {
  const value = useMemo<ConnectedAppsContextValue>(
    () => ({
      providers,
      renderIcon,
      onManage,
      onConnect,
      onSetEnabled,
      webSearchAvailable,
    }),
    [
      providers,
      renderIcon,
      onManage,
      onConnect,
      onSetEnabled,
      webSearchAvailable,
    ]
  );
  return (
    <ConnectedAppsContext.Provider value={value}>
      {children}
    </ConnectedAppsContext.Provider>
  );
}

export function useConnectedApps(): ConnectedAppsContextValue {
  return useContext(ConnectedAppsContext);
}
